import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useHistory } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';

import {
  getRole, updateRolePermissions, getRoleUsers, assignRoleUser, removeRoleUser, updateRoleUserScope,
} from '../../services/roleService';
import api from '../../services/api';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import CheckboxInput from '../../components/ui/CheckboxInput';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import OptionsMenu from '../../components/ui/OptionsMenu';
import RolePositionsTab from './tab-pages/RolePositionsTab';
import DataTable from '../../components/ui/DataTable';
import PersonCell from '../../components/ui/PersonCell';
import {
  buildDataScopeFields, toDataScopePayload, dataScopeInitialValues, formatDataScope, DATA_SCOPE_TYPES,
} from './dataScopeSchema';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

const PermissionsContainer = styled.div`
  padding: 16px;
`;

const PermissionsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.bgColor};
  border-bottom: 1px solid #E5E7EB;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid #F3F4F6;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

// Tek kaydırma: kapsayıcı yüksekliği doldurur, kaydırma yalnız DataTable satırlarında.
const UsersContainer = styled.div`
  padding: 16px;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const UserNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Görev 18 — koltuğu (lisansı) olmayan kullanıcı satırına rozet + /licenses
// bağlantısı. `row.hasSeat` alanı GERÇEKTİR (Kü-9: eski yorum onu "varsayım"
// diye anlatıyordu ve ileride yanlış temizliğe davetiyeydi):
// `Dtos/Role/RoleUserResponse.cs:20` `public bool? HasSeat` ve
// `RoleDataAccess.cs:567` onu rolün ÖLÇÜMLÜ modüllerine göre doldurur.
// `bool?` olması sözleşmenin kendisidir: bağlamda ölçümlü havuz yoksa koltuk
// tablosu hiç okunmaz, alan null kalır ve JSON'a YAZILMAZ (DEĞİŞMEZ 0 —
// RoleDataAccess.cs:468). Alan yoksa rozet SESSİZCE gizlenir, "koltuğu yok"
// diye YALAN söylemez.
// Yetkinin POZİSYONDAN geldiğini gösterir: o satır elle kaldırılamaz.
const SourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 10px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 10px;
  font-weight: 700;
  background: rgba(2, 145, 239, 0.12);
  color: #0268A8;
  white-space: nowrap;
`;

const SeatBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(229, 57, 53, 0.1);
  color: #E53935;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: rgba(229, 57, 53, 0.18); }
`;

export default function RoleDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  // Rol ekranı EM.Write kapılı; pozisyon bağı yazma yetkisi de aynı kapıdan.
  const { canWrite } = useAuth();
  const location = useLocation();
  const history = useHistory();

  const [role, setRole] = useState(null);
  const [activeTab, setActiveTab] = useState('permissions');
  const [permissions, setPermissions] = useState({});
  const [tenantModules, setTenantModules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersFetching, setUsersFetching] = useState(false);
  // AKM POC §9.3 — ekleme ve kapsam düzenleme çekmeceleri (modal YOK).
  const [addOpen, setAddOpen] = useState(false);
  const [scopeRow, setScopeRow] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [removing, setRemoving] = useState(false);

  // Load role details
  useEffect(() => {
    getRole(id).then((res) => {
      const data = res.data?.data;
      if (data) {
        setRole(data);
        // Build permissions map: { moduleCode: permissionValue }
        const permMap = {};
        (data.permissions || []).forEach((p) => {
          permMap[p.moduleCode] = p.permission;
        });
        setPermissions(permMap);
      }
    });
  }, [id]);

  // Load tenant modules
  useEffect(() => {
    api.get('/v1/roles/modules')
      .then((res) => {
        setTenantModules(res.data?.data || []);
      })
      .catch(() => {
        // Modules endpoint might not exist, fallback: use permissions from role
        // We'll build from known module codes
      });
  }, []);

  // Load role users
  const loadUsers = useCallback(() => {
    setUsersFetching(true);
    getRoleUsers(id)
      .then((res) => {
        setUsers(res.data?.data || []);
      })
      .finally(() => setUsersFetching(false));
  }, [id]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const roleName = location.state?.role?.name || role?.name || '...';

  const tabs = [
    { key: 'permissions', label: t('role.permissions') },
    { key: 'users', label: t('role.users') },
    { key: 'positions', label: t('role.positions') },
  ];

  // Permission handling
  const handleReadChange = useCallback((moduleCode, checked) => {
    setPermissions((prev) => {
      const next = { ...prev };
      if (checked) {
        next[moduleCode] = 1; // Read
      } else {
        delete next[moduleCode];
      }
      return next;
    });
  }, []);

  const handleWriteChange = useCallback((moduleCode, checked) => {
    setPermissions((prev) => {
      const next = { ...prev };
      if (checked) {
        next[moduleCode] = 2; // Write (implicitly includes Read)
      } else if (next[moduleCode] === 2) {
        next[moduleCode] = 1; // Downgrade to Read
      }
      return next;
    });
  }, []);

  const handleSavePermissions = useCallback(async () => {
    setSaving(true);
    try {
      const permissionsList = Object.entries(permissions).map(([moduleCode, permission]) => ({
        moduleCode,
        permission,
      }));
      await updateRolePermissions(id, { permissions: permissionsList });
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [id, permissions]);

  // User handling — çekmeceden ekleme (kapsam alanlarıyla). false dönerse çekmece açık kalır.
  // Şirket Yöneticisi ve İK rolü daima tüm şirketi kapsar (sunucu kapsam vermeyi reddeder).
  const isEnterpriseAdminRole = !!role?.isEnterpriseAdmin || !!role?.isHrRole;
  // §9.3 — atama / kapsam eylemleri sunucunun kapısıyla AYNI kuraldan (canEditAssignments / canEditScopes): kısmi kapsamlı
  // İK ya da ayrıcalıklı rolde yönetici olmayan "Yeni" / "Kapsamı Düzenle" görmez (403 almaz). Alan yoksa eski davranış.
  const canEditAssignments = canWrite('EM') && role?.canEditAssignments !== false;
  const canEditScopes = canEditAssignments && role?.canEditScopes !== false && !isEnterpriseAdminRole;
  const scopeLockNotice = role?.isEnterpriseAdmin
    ? t('dataScope.enterpriseAdminAlwaysCompany')
    : t('dataScope.hrRoleAlwaysCompany');

  const handleAssignUser = useCallback(async (formData) => {
    if (!formData?.userId) return false;
    const scope = isEnterpriseAdminRole ? { dataScopeType: DATA_SCOPE_TYPES.DEFAULT } : toDataScopePayload(formData);
    const res = await assignRoleUser(id, { userId: formData.userId, ...scope });
    if (res?.data?.status === false) return false;
    loadUsers();
    return true;
  }, [id, loadUsers, isEnterpriseAdminRole]);

  const handleSaveScope = useCallback(async (formData) => {
    if (!scopeRow?.id) return false;
    const res = await updateRoleUserScope(id, scopeRow.id, toDataScopePayload(formData));
    if (res?.data?.status === false) return false;
    loadUsers();
    return true;
  }, [id, scopeRow, loadUsers]);

  const userSearch = useCallback(async (q) => {
    const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const r = res.data?.data;
    const items = Array.isArray(r) ? r : (r?.data || []);
    return items.map((e) => ({ id: e.userId || e.id, name: `${e.firstName} ${e.lastName}`, email: e.corporateEmail, profilePicture: e.profilePicture }));
  }, []);

  const addSchema = React.useMemo(() => [
    { name: 'userId', label: t('role.addUser'), type: 'asyncSelect', grid: 12, required: true, onSearch: userSearch },
    ...(isEnterpriseAdminRole
      ? [{ name: 'eaNotice', type: 'notice', content: scopeLockNotice }]
      : buildDataScopeFields(t)),
  ], [t, userSearch, isEnterpriseAdminRole, scopeLockNotice]);

  const scopeSchema = React.useMemo(
    () => buildDataScopeFields(t, scopeRow?.dataScopeTargets || []),
    [t, scopeRow],
  );
  // Çekmece initialValues değişince formu sıfırlar — kimlik sabit kalmalı (satır başına bir kez).
  const scopeInitialValues = React.useMemo(() => (scopeRow ? dataScopeInitialValues(scopeRow) : undefined), [scopeRow]);

  const handleRemoveUser = useCallback((row) => {
    setRemoveConfirm({
      isOpen: true,
      id: row.id,
      name: `${row.firstName} ${row.lastName}`,
    });
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoving(true);
    try {
      await removeRoleUser(id, removeConfirm.id);
      loadUsers();
    } catch {
      // error handled by interceptor
    } finally {
      setRemoveConfirm({ isOpen: false, id: null, name: '' });
      setRemoving(false);
    }
  }, [id, removeConfirm.id, loadUsers]);

  // Build module list for permissions tab
  // Use role permissions + tenant modules
  const moduleList = React.useMemo(() => {
    if (tenantModules.length > 0) return tenantModules;
    // Fallback: build from known role permissions
    return (role?.permissions || []).map((p) => ({
      moduleCode: p.moduleCode,
      moduleName: p.moduleName,
    }));
  }, [tenantModules, role]);

  const userColumns = React.useMemo(() => [
    { name: 'id', label: 'ID', options: { display: 'excluded' } },
    {
      name: 'fullName',
      label: t('employee.fullName'),
      options: {
        customBodyRender: (_, tableMeta) => {
          const row = users[tableMeta.rowIndex];
          if (!row) return '';
          return (
            <UserNameCell>
              <PersonCell
                name={`${row.firstName} ${row.lastName}`}
                profilePicture={row.profilePicture}
              />
              {row.fromPosition && (
                <SourceBadge
                  data-testid="role-user-from-position"
                  title={row.positionLabel || t('role.sourcePosition')}
                >
                  {t('role.sourcePosition')}
                </SourceBadge>
              )}
              {row.hasSeat === false && (
                <SeatBadge
                  onClick={(e) => { e.stopPropagation(); history.push('/licenses'); }}
                  title={t('license.noSeat')}
                >
                  {t('license.noSeat')}
                </SeatBadge>
              )}
            </UserNameCell>
          );
        },
      },
    },
    { name: 'email', label: t('employee.email') },
    {
      // AKM POC §9.3 — atamanın veri kapsamı (pozisyondan gelende pozisyon rolünün kapsamı).
      name: 'dataScope',
      label: t('dataScope.title'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, tableMeta) => {
          const row = users[tableMeta.rowIndex];
          if (!row) return '';
          return isEnterpriseAdminRole ? t('dataScope.types.company') : formatDataScope(t, row);
        },
      },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = users[tableMeta.rowIndex];
          // POZİSYONDAN gelen sahipte kaldırılacak atama satırı YOKTUR (yanıtta `id`
          // null gelir). Eylemi göstermek kullanıcıyı çalışmayan bir butona sürüklerdi;
          // yetkiyi kaldırmak için pozisyon bağını çözmek gerekir. Kapsam da pozisyon sekmesinde düzenlenir.
          if (!row || row.fromPosition || !canEditAssignments) return null;
          const actions = canEditScopes ? [
            { type: 'edit', label: t('dataScope.editScope'), onClick: (r) => setScopeRow(r) },
          ] : [];
          actions.push({ type: 'delete', label: t('role.removeUser'), onClick: handleRemoveUser });
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [t, users, handleRemoveUser, isEnterpriseAdminRole, history, canEditAssignments, canEditScopes]);

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('role.title'), path: '/roles' },
          { label: roleName },
        ]} />
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
        {activeTab === 'permissions' && (
          <PermissionsContainer>
            <PermissionsTable>
              <thead>
                <tr>
                  <Th>{t('role.module')}</Th>
                  <Th>{t('role.read')}</Th>
                  <Th>{t('role.write')}</Th>
                </tr>
              </thead>
              <tbody>
                {moduleList.map((mod) => {
                  const code = mod.moduleCode || mod.code;
                  const name = mod.moduleName || mod.name;
                  const perm = role?.isEnterpriseAdmin ? 2 : (permissions[code] || 0);
                  const isReadOnly = role?.isEnterpriseAdmin;
                  return (
                    <tr key={code}>
                      <Td>{name}</Td>
                      <Td>
                        <CheckboxInput
                          checked={perm >= 1}
                          onChange={(checked) => handleReadChange(code, checked)}
                          disabled={isReadOnly}
                        />
                      </Td>
                      <Td>
                        <CheckboxInput
                          checked={perm >= 2}
                          onChange={(checked) => handleWriteChange(code, checked)}
                          disabled={isReadOnly}
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </PermissionsTable>
            {role?.isEnterpriseAdmin ? (
              <SaveRow>
                <span style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 13, color: '#64748b' }}>
                  {t('role.enterpriseAdminPermissionsReadOnly')}
                </span>
              </SaveRow>
            ) : (
              <SaveRow>
                <Button onClick={handleSavePermissions} disabled={saving}>
                  {t('role.savePermissions')}
                </Button>
              </SaveRow>
            )}
          </PermissionsContainer>
        )}

        {activeTab === 'users' && (
          <UsersContainer>
            <GenericFormDrawer
              open={addOpen}
              onClose={() => setAddOpen(false)}
              title={t('role.addUser')}
              schema={addSchema}
              onSave={handleAssignUser}
            />

            <GenericFormDrawer
              open={!!scopeRow}
              onClose={() => setScopeRow(null)}
              title={t('dataScope.editScope')}
              schema={scopeSchema}
              initialValues={scopeInitialValues}
              onSave={handleSaveScope}
            />

            <ConfirmDialog
              isOpen={removeConfirm.isOpen}
              title={t('role.removeUser')}
              message={t('role.removeUserMessage', { name: removeConfirm.name })}
              onConfirm={handleRemoveConfirm}
              onCancel={() => setRemoveConfirm({ isOpen: false, id: null, name: '' })}
              loading={removing}
            />

            <DataTable
              title={t('role.users')}
              fetching={usersFetching}
              columns={userColumns}
              data={users}
              totalRowCount={users.length}
              pageIndex={0}
              pageSize={users.length || 10}
              serverSide={false}
              canCreateNewRecord={canEditAssignments}
              newButtonLabel={t('role.addUser')}
              onNewButtonClick={() => setAddOpen(true)}
            />
          </UsersContainer>
        )}

        {activeTab === 'positions' && (
          <RolePositionsTab
            roleId={id}
            canWrite={canEditAssignments}
            canEditScopes={canEditScopes}
            isEnterpriseAdminRole={isEnterpriseAdminRole}
            scopeLockNotice={scopeLockNotice}
          />
        )}
      </TabContent>
    </PageContainer>
  );
}
